import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { X, Calendar, CalendarClock } from "lucide-react";
import BookingCalendarGrid from "../BookingCalendarGrid";

const ACTIVE = new Set(["bokad", "godkand", "utlamnad"]);

function isoLocal(d) {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 16);
}
function plusHoursFromNow(h) {
  const d = new Date();
  d.setHours(d.getHours() + h, 0, 0, 0);
  return isoLocal(d);
}

export default function TrailerBookingModal({ trailer, onClose, onCreated }) {
  const { t } = useI18n();
  const [start, setStart] = useState(plusHoursFromNow(1));
  const [slut, setSlut] = useState(plusHoursFromNow(5));
  const [projectId, setProjectId] = useState("");
  const [responsibleId, setResponsibleId] = useState("");
  const [note, setNote] = useState("");
  const [projects, setProjects] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const rental = !!trailer.enable_rental_reporting;

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/trailer-bookings", { params: { trailer_id: trailer.id } })
      .then((r) => setBookings((r.data || []).filter((b) => ACTIVE.has(b.status))))
      .catch(() => {});
    if (rental) api.get("/org/members").then((r) => setOrgUsers(r.data || [])).catch(() => {});
  }, [rental, trailer.id]);

  const activeUpcoming = useMemo(() => {
    const now = new Date();
    return bookings
      .filter((b) => new Date(b.slut) > now)
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [bookings]);

  const conflict = useMemo(() => {
    if (!start || !slut) return null;
    const s = new Date(start);
    const e = new Date(slut);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e <= s) return null;
    return activeUpcoming.find((b) => {
      const bs = new Date(b.start);
      const be = new Date(b.slut);
      return bs < e && be > s;
    }) || null;
  }, [start, slut, activeUpcoming]);

  const submit = async (e) => {
    e.preventDefault();
    if (conflict) {
      toast.error(t("mbk.collision"));
      return;
    }
    if (rental && (!projectId || !responsibleId)) {
      toast.error(t("rental.requiredHint"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/trailer-bookings", {
        trailer_id: trailer.id,
        start: new Date(start).toISOString(),
        slut: new Date(slut).toISOString(),
        project_id: projectId || null,
        responsible_user_id: responsibleId || null,
        note,
      });
      toast.success(t("mbk.created"));
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4 overflow-auto" onClick={onClose} data-testid="trailer-booking-modal">
      <div className="bg-[var(--bone)] border border-[var(--ink-200)] max-w-[640px] w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--ink-200)]">
          <div className="font-display text-xl inline-flex items-center gap-2">
            <Calendar size={16} /> {t("trl.book")} — {trailer.regnr}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--surface-alt)]"><X size={16} /></button>
        </div>

        {/* Visual booking calendar — click free/partial day to set 08:00–17:00 */}
        <div className="p-4 border-b border-[var(--ink-200)]">
          <div className="label-tech inline-flex items-center gap-1 mb-2">
            <CalendarClock size={12} /> {t("mbk.cal.title")}
          </div>
          <BookingCalendarGrid
            bookings={activeUpcoming}
            onPickDay={(d) => {
              const s = new Date(d); s.setHours(8, 0, 0, 0);
              const e = new Date(d); e.setHours(17, 0, 0, 0);
              const tz = s.getTimezoneOffset() * 60000;
              setStart(new Date(s.getTime() - tz).toISOString().slice(0, 16));
              setSlut(new Date(e.getTime() - tz).toISOString().slice(0, 16));
            }}
          />
          {conflict && (
            <div className="mt-3 px-3 py-2 border-2 text-sm" style={{ borderColor: "var(--brick)", background: "color-mix(in srgb, var(--brick) 12%, var(--surface))" }} data-testid="trailer-conflict-warn">
              <span className="font-semibold">{t("mbk.cal.conflict")}</span>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-tech mb-2">{t("mbk.start")}</div>
            <input type="datetime-local" className="input-tech" value={start} onChange={(e) => setStart(e.target.value)} required data-testid="trailer-start-input"
              style={conflict ? { borderColor: "var(--brick)" } : undefined} />
          </div>
          <div>
            <div className="label-tech mb-2">{t("mbk.end")}</div>
            <input type="datetime-local" className="input-tech" value={slut} onChange={(e) => setSlut(e.target.value)} required data-testid="trailer-end-input"
              style={conflict ? { borderColor: "var(--brick)" } : undefined} />
          </div>
          <div>
            <div className="label-tech mb-2">{t("prj.selectProject")}{rental && <span className="text-[var(--brick)] ml-1">*</span>}</div>
            <select className="input-tech" value={projectId} onChange={(e) => setProjectId(e.target.value)} required={rental} data-testid="trailer-project-select">
              <option value="">—</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.namn}</option>)}
            </select>
          </div>
          {rental && (
            <div data-testid="trailer-rental-block">
              <div className="label-tech mb-2">{t("rental.responsible")} <span className="text-[var(--brick)]">*</span></div>
              <select className="input-tech" value={responsibleId} onChange={(e) => setResponsibleId(e.target.value)} required data-testid="trailer-responsible-select">
                <option value="">—</option>
                {orgUsers.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
              </select>
              <div className="text-[10px] text-[var(--clay)] mt-2 uppercase tracking-wider font-mono-tech">{t("rental.willGenerate")}</div>
            </div>
          )}
          <div className="md:col-span-2">
            <div className="label-tech mb-2">{t("mbk.note")}</div>
            <textarea rows={2} className="input-tech" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>{t("admin.orgs.cancel")}</button>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="trailer-submit-booking">{t("admin.orgs.save")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
